import React, { useState } from 'react';
import { MapPin, DollarSign, Users, BookmarkPlus, Bookmark, Eye, Calendar, Briefcase } from 'lucide-react';
import { saveJob, unsaveJob } from '../../../services/jobService';

const JobCard = ({ job, onViewDetails }) => {
  const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSaveJob = async (e) => {
    e?.stopPropagation();
    
    try {
      setSaving(true);
      if (isSaved) {
        await unsaveJob(job?.id);
        setIsSaved(false);
      } else {
        await saveJob(job?.id);
        setIsSaved(true);
      }
    } catch (error) {
      console.error('Error saving job:', error);
      // Could add toast notification here
    } finally {
      setSaving(false);
    }
  };

  const formatSalary = (min, max, currency = 'USD') => {
    if (!min && !max) return null;
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    
    if (min && max) {
      return `${formatter?.format(min)} - ${formatter?.format(max)}`;
    }
    return formatter?.format(min || max);
  };

  const formatJobType = (type) => {
    return type?.replace('_', ' ')?.split(' ')?.map(word => 
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ') || '';
  };

  const formatExperienceLevel = (level) => {
    return level?.replace('_', ' ')?.split(' ')?.map(word => 
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ') || '';
  };

  const formatCategory = (category) => {
    return category?.replace('_', ' ')?.split(' ')?.map(word => 
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ') || '';
  };

  const isDeadlineNear = (deadline) => {
    if (!deadline) return false;
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffDays = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays >= 0;
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return null;
    return new Date(deadline)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div 
      className="bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer p-6"
      onClick={onViewDetails}
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
            {job?.title}
          </h3>
          <p className="text-gray-600 text-sm mb-2">
            {job?.user_profiles?.organization || job?.user_profiles?.full_name}
          </p>
        </div>
        
        <button
          onClick={handleSaveJob}
          disabled={saving}
          className="ml-2 p-2 text-gray-400 hover:text-blue-600 transition-colors"
          title={isSaved ? 'Remove from saved' : 'Save job'}
        >
          {isSaved ? (
            <Bookmark className="w-5 h-5 fill-current" />
          ) : (
            <BookmarkPlus className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Job Details */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center text-sm text-gray-600">
          <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
          <span>{job?.location}</span>
          {job?.is_remote && (
            <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
              Remote
            </span>
          )}
        </div>
        
        <div className="flex items-center text-sm text-gray-600">
          <Briefcase className="w-4 h-4 mr-2 flex-shrink-0" />
          <span>{formatJobType(job?.job_type)}</span>
        </div>
        
        <div className="flex items-center text-sm text-gray-600">
          <Users className="w-4 h-4 mr-2 flex-shrink-0" />
          <span>{formatExperienceLevel(job?.experience_level)}</span>
        </div>

        {(job?.salary_min || job?.salary_max) && (
          <div className="flex items-center text-sm text-gray-600">
            <DollarSign className="w-4 h-4 mr-2 flex-shrink-0" />
            <span>{formatSalary(job?.salary_min, job?.salary_max, job?.currency)}</span>
          </div>
        )}
      </div>

      {/* Job Category Badge */}
      <div className="mb-4">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {formatCategory(job?.category)}
        </span>
      </div>

      {/* Job Description Preview */}
      <p className="text-sm text-gray-600 mb-4 line-clamp-3">
        {job?.description}
      </p>

      {/* Skills Preview */}
      {job?.skills_required?.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {job?.skills_required?.slice(0, 3)?.map((skill, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
              >
                {skill}
              </span>
            ))}
            {job?.skills_required?.length > 3 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded">
                +{job?.skills_required?.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t">
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <Eye className="w-4 h-4 mr-1" />
            <span>{job?.view_count || 0} views</span>
          </div>
          <div className="flex items-center">
            <Users className="w-4 h-4 mr-1" />
            <span>{job?.application_count || 0} applicants</span>
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-xs text-gray-400">
            Posted {new Date(job?.created_at)?.toLocaleDateString()}
          </div>
          {job?.application_deadline && (
            <div className={`text-xs ${isDeadlineNear(job?.application_deadline) ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
              <Calendar className="w-3 h-3 inline mr-1" />
              Deadline: {formatDeadline(job?.application_deadline)}
              {isDeadlineNear(job?.application_deadline) && (
                <span className="ml-1 text-red-600">⚠️</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Featured Badge */}
      {job?.is_featured && (
        <div className="absolute top-4 right-4 bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">
          Featured
        </div>
      )}
    </div>
  );
};

export default JobCard;